import { useEffect } from 'modules/core/hooks/useEffect'

const NAVATTIC_APP_HOSTNAME = 'app.factorialhr.com'
const NAVATTIC_HOST_SUFFIX = '.navattic.com'
const NAVATTIC_HOSTNAME = 'factorial.navattic.com'

const getMessageUrl = (data: unknown): string | null => {
  if (typeof data === 'string') return data

  if (data && typeof data === 'object') {
    const isRecord = (value: object): value is Record<string, unknown> =>
      Object.prototype.toString.call(value) === '[object Object]'

    if (!isRecord(data)) return null

    const directUrl = data.url ?? data.href ?? data.location
    if (typeof directUrl === 'string') return directUrl

    const payload = data.payload
    if (payload && typeof payload === 'object' && isRecord(payload)) {
      if (typeof payload.url === 'string') return payload.url
      if (typeof payload.href === 'string') return payload.href
    }
  }

  return null
}

const isAllowedOrigin = (origin: string) => {
  try {
    const { hostname } = new URL(origin)
    return hostname === NAVATTIC_HOSTNAME || hostname.endsWith(NAVATTIC_HOST_SUFFIX)
  } catch {
    return false
  }
}

const isNavatticIframeSource = (event: MessageEvent) => {
  const iframe = document.querySelector<HTMLIFrameElement>('iframe[data-navattic-demo-id]')
  if (!iframe?.contentWindow) return false

  return event.source === iframe.contentWindow
}

const isAppRedirectUrl = (messageUrl: string) => {
  try {
    const { hostname } = new URL(messageUrl, window.location.origin)
    return hostname === NAVATTIC_APP_HOSTNAME
  } catch {
    return false
  }
}

const useNavatticIframeRedirect = () => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const messageUrl = getMessageUrl(event.data)
      if (!messageUrl) return

      if (!isAllowedOrigin(event.origin)) return
      if (!isNavatticIframeSource(event)) return

      if (isAppRedirectUrl(messageUrl)) {
        document.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true,
            cancelable: true,
          })
        )
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])
}

export default useNavatticIframeRedirect
